/*
 * Copyright 2020 Google LLC
 *
 * Licensed under the Apache License, Version 2.0 (the "License");
 * you may not use this file except in compliance with the License.
 * You may obtain a copy of the License at
 *
 *     https://www.apache.org/licenses/LICENSE-2.0
 *
 * Unless required by applicable law or agreed to in writing, software
 * distributed under the License is distributed on an "AS IS" BASIS,
 * WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
 * See the License for the specific language governing permissions and
 * limitations under the License.
 */

/**
 * External dependencies
 */
import {
  useEffect,
  useState,
  useCallback,
  useMemo,
  useRef,
} from '@web-stories-wp/react';

/**
 * Internal dependencies
 */
import { MEDIA_VIDEO_MINIMUM_DURATION } from '../../constants';

function useVideoNode({
  fixedDurationMs = null,
  initialStartOffset = 0,
  initialEndOffset = null,
  onDurationChange,
} = {}) {
  const [currentTime, setCurrentTime] = useState(null);
  const [startOffset, rawSetStartOffset] = useState(null);
  const [originalStartOffset, setOriginalStartOffset] = useState(null);
  const [endOffset, rawSetEndOffset] = useState(null);
  const [originalEndOffset, setOriginalEndOffset] = useState(null);
  const [isLooping, setIsLooping] = useState(true);
  const [maxOffset, setMaxOffset] = useState(null);
  const [videoNode, setVideoNode] = useState(null);
  const initializedVideoNode = useRef(null);

  // Until its metadata loads, a video must not show the last video's range or
  // let Done save it.
  useEffect(() => {
    if (videoNode === initializedVideoNode.current) {
      return;
    }

    setCurrentTime(null);
    rawSetStartOffset(null);
    setOriginalStartOffset(null);
    rawSetEndOffset(null);
    setOriginalEndOffset(null);
    setMaxOffset(null);
  }, [videoNode]);

  useEffect(() => {
    if (!videoNode) {
      return undefined;
    }

    function onLoadedMetadata(evt) {
      initializedVideoNode.current = videoNode;
      const duration = Math.floor(evt.target.duration * 1000);
      const maxStart = Number.isFinite(fixedDurationMs)
        ? Math.max(0, duration - fixedDurationMs)
        : 0;
      const nextStart = Number.isFinite(fixedDurationMs)
        ? Math.min(Math.max(0, initialStartOffset), maxStart)
        : Math.min(Math.max(0, initialStartOffset), duration);
      const nextEnd = Number.isFinite(fixedDurationMs)
        ? Math.min(duration, nextStart + fixedDurationMs)
        : Math.min(duration, initialEndOffset ?? duration);

      rawSetStartOffset(nextStart);
      setOriginalStartOffset(nextStart);
      setCurrentTime(nextStart);
      rawSetEndOffset(nextEnd);
      setOriginalEndOffset(nextEnd);
      setMaxOffset(duration);
      videoNode.currentTime = nextStart / 1000;
      onDurationChange?.(duration / 1000);
    }
    function onTimeUpdate(evt) {
      const currentOffset = Math.floor(evt.target.currentTime * 1000);
      if (isLooping && currentOffset >= endOffset && !videoNode.paused) {
        setCurrentTime(startOffset);
        videoNode.currentTime = startOffset / 1000;
        videoNode.play();
      } else {
        setCurrentTime(currentOffset);
      }
    }
    videoNode.addEventListener('timeupdate', onTimeUpdate);
    videoNode.addEventListener('loadedmetadata', onLoadedMetadata);

    if (
      videoNode.readyState >= 1 &&
      initializedVideoNode.current !== videoNode
    ) {
      onLoadedMetadata({ target: videoNode });
    }

    return () => {
      videoNode.removeEventListener('timeupdate', onTimeUpdate);
      videoNode.removeEventListener('loadedmetadata', onLoadedMetadata);
    };
  }, [
    startOffset,
    endOffset,
    fixedDurationMs,
    initialEndOffset,
    initialStartOffset,
    isLooping,
    onDurationChange,
    videoNode,
  ]);

  const setStartOffset = useCallback(
    (offset) => {
      const maximumStart = Number.isFinite(fixedDurationMs)
        ? maxOffset - fixedDurationMs
        : endOffset - MEDIA_VIDEO_MINIMUM_DURATION;
      offset = Math.min(maximumStart, offset);
      offset = Math.max(0, offset);
      rawSetStartOffset(offset);
      if (Number.isFinite(fixedDurationMs)) {
        rawSetEndOffset(Math.min(maxOffset, offset + fixedDurationMs));
      } else {
        videoNode.currentTime = Math.max(videoNode.currentTime, offset / 1000);
      }
    },
    [videoNode, endOffset, fixedDurationMs, maxOffset]
  );

  const setEndOffset = useCallback(
    (offset) => {
      if (Number.isFinite(fixedDurationMs)) {
        return;
      }

      // End offset must be at least this larger than start offset
      offset = Math.max(startOffset + MEDIA_VIDEO_MINIMUM_DURATION, offset);
      offset = Math.min(maxOffset, offset);
      rawSetEndOffset(offset);
      videoNode.currentTime = Math.min(videoNode.currentTime, offset / 1000);
    },
    [videoNode, fixedDurationMs, startOffset, maxOffset]
  );

  const resetOffsets = useCallback(() => {
    rawSetStartOffset(originalStartOffset);
    rawSetEndOffset(originalEndOffset);
    setCurrentTime(originalStartOffset);

    if (videoNode && Number.isFinite(originalStartOffset)) {
      videoNode.currentTime = originalStartOffset / 1000;
    }
  }, [originalEndOffset, originalStartOffset, videoNode]);

  const commitOffsets = useCallback(() => {
    setOriginalStartOffset(startOffset);
    setOriginalEndOffset(endOffset);
  }, [endOffset, startOffset]);

  const hasChanged = useMemo(
    () =>
      startOffset !== originalStartOffset || endOffset !== originalEndOffset,
    [startOffset, originalStartOffset, endOffset, originalEndOffset]
  );

  return {
    hasChanged,
    currentTime,
    isLooping,
    startOffset,
    endOffset,
    maxOffset,
    setStartOffset,
    setEndOffset,
    setIsLooping,
    setVideoNode,
    commitOffsets,
    resetOffsets,
  };
}

export default useVideoNode;
